import { useMemo, useState } from "react";
import { BriefcaseFill, Person, PersonCircle } from "react-bootstrap-icons";
import ReactSelect, {
  type MenuPlacement,
  type StylesConfig,
} from "react-select";
import { groupBy } from "@/lib/utils";

type Option = { label: string; value: string };
type Group = { key: string; options: Option[] };

export interface VariantOption {
  label: string;
  value: string | null;
  key: string;
}

interface Props {
  options: VariantOption[];
  className?: string;
  selected?: string;
  onChange: (value: string | undefined) => void;
  onClickAdd?: () => void;
  deemphasizeSelected?: boolean;
  openPos?: MenuPlacement;
  isClearable?: boolean;
}

export function formatGroupLabelString(key: string) {
  if (key.indexOf("Firm") > -1) {
    return (
      <div>
        <BriefcaseFill />
        <span style={{ paddingLeft: 5, verticalAlign: "middle" }}>{key}</span>
      </div>
    );
  }

  if (key.indexOf("Personal") > -1) {
    return (
      <div>
        <PersonCircle />
        <span style={{ paddingLeft: 5, verticalAlign: "middle" }}>{key}</span>
      </div>
    );
  }

  if (key) {
    return (
      <div>
        <Person />
        <span style={{ paddingLeft: 5, verticalAlign: "middle" }}>{key}</span>
      </div>
    );
  }

  return <div>{key}</div>;
}

export default function Select({
  onClickAdd,
  options,
  className,
  selected,
  onChange,
  deemphasizeSelected,
  openPos,
  isClearable,
}: Props) {
  const [selectedOption, setSelectedOption] = useState<
    VariantOption | undefined
  >(options.find((o) => o.value === selected));

  const customStyles: StylesConfig<Option, false, Group> = useMemo(() => {
    return {
      indicatorSeparator: () => ({
        display: "none",
      }),
      option: (provided) => ({
        ...provided,
        padding: "2px 8px",
      }),
      singleValue: (provided) => ({
        ...provided,
        color: deemphasizeSelected ? "grey" : undefined,
      }),
      group: (provided) => ({
        ...provided,
        padding: "0px",
        ...(onClickAdd
          ? {
              ":last-child": {
                borderTop: "1pt solid #cccccc",
              },
            }
          : {}),
      }),
      groupHeading: (provided, state) => {
        if (!state.data?.key) {
          return provided;
        }

        return {
          ...provided,
          borderTop: "1pt solid #cccccc",
          borderBottom: "1pt solid #cccccc",
        };
      },
    };
  }, [onClickAdd, deemphasizeSelected]);

  const transformedOptions = useMemo(() => {
    const group = groupBy(options, (i) => i.key);
    const opts = [];

    for (const k in group) {
      opts.push({
        key: k,
        options: group[k].map((v) => ({
          label: v.label,
          value: v.value ?? "",
        })),
      });
    }

    if (onClickAdd) {
      opts.push({ key: "", options: [{ label: "+ Add New", value: "" }] });
    }

    return opts;
  }, [onClickAdd, options]);

  const handleChange = (option: Option | null) => {
    if (option === null) {
      setSelectedOption(undefined);
      onChange(undefined);
    } else {
      const variantOption = { ...option, key: "" };
      setSelectedOption(variantOption);
      if (option.value) {
        onChange(option.value);
      } else if (option.label === "+ Add New" && onClickAdd) {
        onClickAdd();
      }
    }
  };

  const formatGroupLabel = (option: Group) => {
    return formatGroupLabelString(option.key);
  };

  return (
    <ReactSelect
      className={className}
      classNamePrefix="brief-point-select"
      options={transformedOptions}
      styles={customStyles}
      value={
        selectedOption
          ? { label: selectedOption.label, value: selectedOption.value ?? "" }
          : null
      }
      formatGroupLabel={formatGroupLabel}
      onChange={handleChange}
      theme={(theme) => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: "#074f6b",
          primary25: "#efefea",
        },
      })}
      //menuIsOpen={true}
      menuPlacement={openPos ?? "top"}
      isClearable={(isClearable ?? false) && !!selectedOption}
    />
  );
}
